import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Users, Award, TrendingUp } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePageTitle } from "@/lib/title";
import { api } from "@/lib/api";
import { Card, CardBody } from "@/components/ui/Card";
import { FullPageSpinner } from "@/components/ui/Spinner";
import type { AnalyticsOverview, Enrollment, Course } from "@/types";

function StatCard({ icon: Icon, label, value }: { icon: typeof BookOpen; label: string; value: string | number }) {
  return (
    <Card>
      <CardBody className="flex items-center gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function StudentDashboard() {
  const { data: enrollments, isLoading } = useQuery({
    queryKey: ["enrollments", "me"],
    queryFn: () => api.get<Enrollment[]>("/api/v1.0/enrollments/me"),
  });

  if (isLoading) return <FullPageSpinner />;

  const inProgress = enrollments?.filter((e) => e.status === "active") ?? [];
  const completed = enrollments?.filter((e) => e.status === "completed") ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={BookOpen} label="Enrolled courses" value={enrollments?.length ?? 0} />
        <StatCard icon={TrendingUp} label="In progress" value={inProgress.length} />
        <StatCard icon={Award} label="Completed" value={completed.length} />
      </div>

      <Card>
        <CardBody>
          <h2 className="mb-4 font-semibold text-slate-900">Continue learning</h2>
          {inProgress.length === 0 ? (
            <p className="text-sm text-slate-500">
              You have no courses in progress.{" "}
              <Link to="/courses" className="text-brand-600 hover:underline">
                Browse the catalog
              </Link>
              .
            </p>
          ) : (
            <div className="flex flex-col divide-y divide-slate-100">
              {inProgress.map((e) => (
                <Link
                  key={e.id}
                  to={`/courses/${e.course_id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50"
                >
                  <span className="font-medium text-slate-800">{e.course_title}</span>
                  <div className="flex items-center gap-3">
                    <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full bg-brand-600" style={{ width: `${e.progress_percent}%` }} />
                    </div>
                    <span className="text-sm text-slate-500">{e.progress_percent}%</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StaffDashboard() {
  const { data: overview, isLoading: loadingOverview } = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: () => api.get<AnalyticsOverview>("/api/v1.0/analytics/overview"),
  });
  const { data: courses, isLoading: loadingCourses } = useQuery({
    queryKey: ["courses", { page: 1 }],
    queryFn: () => api.getPaginated<Course>("/api/v1.0/courses?page=1&page_size=5"),
  });

  if (loadingOverview || loadingCourses) return <FullPageSpinner />;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={BookOpen} label="Published courses" value={overview?.published_courses ?? 0} />
        <StatCard icon={Users} label="Total students" value={overview?.total_students ?? 0} />
        <StatCard icon={GraduationCap} label="Total enrollments" value={overview?.total_enrollments ?? 0} />
        <StatCard icon={Award} label="Certificates issued" value={overview?.certificates_issued ?? 0} />
      </div>

      <Card>
        <CardBody>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-slate-900">Recent courses</h2>
            <Link to="/courses" className="text-sm text-brand-600 hover:underline">
              View all
            </Link>
          </div>
          <div className="flex flex-col divide-y divide-slate-100">
            {courses?.data.map((c) => (
              <Link key={c.id} to={`/courses/${c.id}`} className="flex items-center justify-between py-3 hover:bg-slate-50">
                <span className="font-medium text-slate-800">{c.title}</span>
                <span className="text-sm capitalize text-slate-500">{c.status}</span>
              </Link>
            ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const firstName = user?.full_name?.split(" ")[0];
  usePageTitle(firstName ? `Welcome back, ${firstName}` : "Dashboard");

  return (
    <div className="flex flex-col gap-6">
      <p className="-mt-3 text-sm text-slate-500">Here&apos;s what&apos;s happening in your learning platform.</p>
      {user?.role === "student" ? <StudentDashboard /> : <StaffDashboard />}
    </div>
  );
}
